'use client';

import { useEffect } from 'react';
import type { LandingPageSettingsFirebaseType } from '@poppy/firebase';

function appendCode(target: HTMLElement, html: string) {
  const template = document.createElement('template');
  template.innerHTML = html;
  // Scripts parsed via innerHTML are inert. Recreate them, including nested snippets.
  for (const source of Array.from(template.content.querySelectorAll('script'))) {
    const script = document.createElement('script');
    for (const attribute of Array.from(source.attributes))
      script.setAttribute(attribute.name, attribute.value);
    script.textContent = source.textContent;
    source.replaceWith(script);
  }
  const nodes = Array.from(template.content.childNodes);
  target.append(...nodes);
  return nodes;
}

export default function PageSettingsRuntime({
  settings,
  preview = false,
}: {
  settings: LandingPageSettingsFirebaseType;
  preview?: boolean;
}) {
  useEffect(() => {
    const mounted: Node[] = preview
      ? []
      : [
          ...appendCode(document.head, settings.customCode.head),
          ...appendCode(document.body, settings.customCode.bodyEnd),
        ];
    const style = document.createElement('style');
    const { fontFamily, accentColor } = settings.brand;
    const font = fontFamily === 'system-ui' ? 'system-ui' : JSON.stringify(fontFamily);
    const fallbackFont =
      fontFamily === 'Georgia'
        ? 'serif'
        : fontFamily === 'Courier New'
          ? 'monospace'
          : 'sans-serif';
    style.textContent = [
      fontFamily
        ? `.poppy-published-page, .poppy-published-page :is(h1,h2,h3,h4,h5,h6,button,input,select,textarea){font-family:${font},${fallbackFont}!important}`
        : '',
      accentColor
        ? `.poppy-published-page{--poppy-accent-color:${accentColor}} .poppy-published-page :is(button,input[type="submit"],[role="button"],a.btn,a.button,a.cta,a[class*="btn-"],a[class*="button-"],a[class*="cta-"]){background-color:${accentColor}!important}`
        : '',
    ].join('\n');
    document.head.append(style);
    mounted.push(style);
    const addScript = (text: string, src?: string) => {
      const script = document.createElement('script');
      if (src) {
        script.src = src;
        script.async = true;
      } else script.textContent = text;
      document.head.append(script);
      mounted.push(script);
    };
    const ga = settings.tracking.googleAnalyticsId;
    if (!preview && /^G-[A-Z0-9]+$/i.test(ga)) {
      addScript('', `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ga)}`);
      addScript(
        `window.dataLayer=window.dataLayer||[];window.gtag=window.gtag||function(){window.dataLayer.push(arguments)};window.gtag('js',new Date());window.gtag('config',${JSON.stringify(ga)});`
      );
    }
    const pixel = settings.tracking.metaPixelId;
    if (!preview && /^\d{5,30}$/.test(pixel)) {
      addScript(
        `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;t.dataset.poppyPixelLoader='true';b.head.appendChild(t)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init',${JSON.stringify(pixel)});fbq('trackSingle',${JSON.stringify(pixel)},'PageView');`
      );
    }
    return () => {
      mounted.forEach(node => node.parentNode?.removeChild(node));
      document.querySelectorAll('[data-poppy-pixel-loader]').forEach(node => node.remove());
    };
  }, [settings, preview]);
  return null;
}
